import { useState, useEffect } from 'react';
import { Check, MapPin, Clock, Trophy, RotateCcw, Download, Upload, Smartphone, Award } from 'lucide-react';
import { triggerChrisMorganAwardCelebration } from './ConfettiCelebration';
import type { Building } from '../types/Building';

interface ChecklistProps {
  buildings: Building[];
}

interface VisitedBuilding {
  rank: number;
  visitedAt: string;
  notes?: string;
}

export function SupertallsChecklist({ buildings }: ChecklistProps) {
  const [visitedBuildings, setVisitedBuildings] = useState<VisitedBuilding[]>([]);
  const [notes, setNotes] = useState<Record<number, string>>({});
  const [awardedBuildings, setAwardedBuildings] = useState<number[]>([]);
  const [showCompleted, setShowCompleted] = useState(true);
  const [sortBy, setSortBy] = useState<'rank' | 'height' | 'visited'>('rank');
  const [isLoaded, setIsLoaded] = useState(false);

  // Load progress from localStorage
  useEffect(() => {
    console.log('🔍 Loading progress from localStorage...');
    const saved = localStorage.getItem('supertalls-progress');
    console.log('📦 Raw saved data:', saved);
    
    if (saved) {
      try {
        const data = JSON.parse(saved);
        console.log('✅ Parsed data:', data);
        setVisitedBuildings(data.visited || []);
        setNotes(data.notes || {});
        setAwardedBuildings(data.awarded || []);
        console.log('🎯 Restored:', data.visited?.length || 0, 'visited buildings,', data.awarded?.length || 0, 'awarded buildings');
      } catch (error) {
        console.error('❌ Failed to load progress:', error);
      }
    } else {
      console.log('📭 No saved data found');
    }
    
    setIsLoaded(true);
  }, []);

  // Save progress to localStorage (only after initial load)
  useEffect(() => {
    if (!isLoaded) return; // Don't save on initial load
    
    const data = {
      visited: visitedBuildings,
      notes: notes,
      awarded: awardedBuildings,
      lastUpdated: new Date().toISOString()
    };
    
    try {
      localStorage.setItem('supertalls-progress', JSON.stringify(data));
      console.log('💾 Saved progress:', visitedBuildings.length, 'visited buildings,', awardedBuildings.length, 'awarded buildings');
      console.log('📝 Data saved:', data);
    } catch (error) {
      console.error('❌ Failed to save progress:', error);
    }
  }, [visitedBuildings, notes, awardedBuildings, isLoaded]);

  const toggleVisited = (building: Building) => {
    const isCurrentlyVisited = visitedBuildings.some(v => v.rank === building.rank);
    
    if (isCurrentlyVisited) {
      setVisitedBuildings(prev => prev.filter(v => v.rank !== building.rank));
    } else {
      setVisitedBuildings(prev => [...prev, {
        rank: building.rank,
        visitedAt: new Date().toISOString()
      }]);
    }
  };

  const updateNotes = (rank: number, note: string) => {
    setNotes(prev => ({
      ...prev,
      [rank]: note
    }));
  };

  const toggleAwarded = (building: Building) => {
    const isCurrentlyAwarded = awardedBuildings.includes(building.rank);
    
    if (isCurrentlyAwarded) {
      setAwardedBuildings(prev => prev.filter(rank => rank !== building.rank));
    } else {
      setAwardedBuildings(prev => [...prev, building.rank]);
      // 🎉 CELEBRATE THE CHRIS MORGAN ARCHITECTURAL ACHIEVEMENT AWARD! 🎉
      triggerChrisMorganAwardCelebration();
    }
  };

  const isAwarded = (rank: number) => awardedBuildings.includes(rank);

  const resetProgress = () => {
    if (confirm('Are you sure you want to reset all progress? This cannot be undone.')) {
      setVisitedBuildings([]);
      setNotes({});
      setAwardedBuildings([]);
      localStorage.removeItem('supertalls-progress');
    }
  };

  const exportProgress = () => {
    const data = {
      visited: visitedBuildings,
      notes: notes,
      awarded: awardedBuildings,
      exportedAt: new Date().toISOString(),
      totalBuildings: buildings.length,
      appVersion: '1.0'
    };
    
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `supertalls-progress-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const importProgress = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = JSON.parse(e.target?.result as string);
        if (data.visited && Array.isArray(data.visited)) {
          setVisitedBuildings(data.visited);
          setNotes(data.notes || {});
          setAwardedBuildings(data.awarded || []);
          
          // Show success message
          const importedCount = data.visited.length;
          const awardedCount = (data.awarded || []).length;
          alert(`✅ Successfully imported progress!\n${importedCount} buildings marked as visited, ${awardedCount} awarded.`);
        } else {
          alert('❌ Invalid file format. Please select a valid progress export file.');
        }
      } catch (error) {
        alert('❌ Failed to import file. Please check the file format.');
      }
    };
    reader.readAsText(file);
    
    // Reset file input
    event.target.value = '';
  };

  const isVisited = (rank: number) => visitedBuildings.some(v => v.rank === rank);
  const visitedCount = visitedBuildings.length;
  const awardedCount = awardedBuildings.length;
  const progressPercentage = Math.round((visitedCount / buildings.length) * 100);

  // Sort buildings
  const sortedBuildings = [...buildings].sort((a, b) => {
    switch (sortBy) {
      case 'height':
        return b.height_meters - a.height_meters;
      case 'visited':
        const aVisited = isVisited(a.rank);
        const bVisited = isVisited(b.rank);
        if (aVisited && !bVisited) return showCompleted ? 1 : -1;
        if (!aVisited && bVisited) return showCompleted ? -1 : 1;
        return a.rank - b.rank;
      default:
        return a.rank - b.rank;
    }
  });

  // Filter buildings
  const filteredBuildings = showCompleted ? sortedBuildings : sortedBuildings.filter(b => !isVisited(b.rank));

  const getProgressColor = (percentage: number) => {
    if (percentage === 100) return 'text-green-600 bg-green-100';
    if (percentage >= 75) return 'text-blue-600 bg-blue-100';
    if (percentage >= 50) return 'text-yellow-600 bg-yellow-100';
    if (percentage >= 25) return 'text-orange-600 bg-orange-100';
    return 'text-gray-600 bg-gray-100';
  };

  return (
    <div className="space-y-6">
      {/* Progress Header */}
      <div className="bg-white rounded-lg shadow-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-bold text-gray-900">🏗️ Supertalls Checklist</h2>
          <div className="flex items-center gap-2">
            <button
              onClick={exportProgress}
              className="flex items-center gap-2 px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
              title="Export your progress"
            >
              <Download size={16} />
              Export
            </button>
            <label className="flex items-center gap-2 px-3 py-2 text-sm text-green-600 hover:bg-green-50 rounded-md transition-colors cursor-pointer">
              <Upload size={16} />
              Import
              <input
                type="file"
                accept=".json"
                onChange={importProgress}
                className="hidden"
              />
            </label>
            <button
              onClick={resetProgress}
              className="flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-md transition-colors"
            >
              <RotateCcw size={16} />
              Reset
            </button>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="mb-4">
          <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
            <span>Progress: {visitedCount} of {buildings.length} visited • {awardedCount} awarded 🏆</span>
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getProgressColor(progressPercentage)}`}>
              {progressPercentage}%
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3">
            <div
              className="bg-blue-600 h-3 rounded-full transition-all duration-300"
              style={{ width: `${progressPercentage}%` }}
            />
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
          <div className="bg-gray-50 rounded-lg p-3">
            <div className="text-2xl font-bold text-gray-900">{visitedCount}</div>
            <div className="text-sm text-gray-600">Visited</div>
          </div>
          <div className="bg-gray-50 rounded-lg p-3">
            <div className="text-2xl font-bold text-gray-900">{buildings.length - visitedCount}</div>
            <div className="text-sm text-gray-600">Remaining</div>
          </div>
          <div className="bg-gray-50 rounded-lg p-3">
            <div className="text-2xl font-bold text-gray-900">
              {visitedBuildings.length > 0 ? 
                Math.round(visitedBuildings.reduce((sum, v) => {
                  const building = buildings.find(b => b.rank === v.rank);
                  return sum + (building?.height_meters || 0);
                }, 0) / visitedBuildings.length) : 0}m
            </div>
            <div className="text-sm text-gray-600">Avg Height</div>
          </div>
          <div className="bg-gray-50 rounded-lg p-3">
            <div className="text-2xl font-bold text-gray-900">
              {visitedCount === buildings.length ? '🏆' : '🎯'}
            </div>
            <div className="text-sm text-gray-600">
              {visitedCount === buildings.length ? 'Complete!' : 'In Progress'}
            </div>
          </div>
        </div>

        {visitedCount === buildings.length && (
          <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center gap-2 text-green-800">
              <Trophy size={20} />
              <span className="font-semibold">Congratulations! You've visited all {buildings.length} NYC supertalls!</span>
            </div>
          </div>
        )}

        {/* Storage Status */}
        <div className="mt-4 p-3 bg-gray-50 rounded-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Smartphone size={16} />
              <span>
                Progress automatically saved to your device. 
                {visitedBuildings.length > 0 && (
                  <span className="text-green-600 font-medium ml-1">
                    ✅ {visitedBuildings.length} buildings restored from storage
                  </span>
                )}
                {visitedBuildings.length === 0 && isLoaded && (
                  <span className="text-gray-500 ml-1">No progress yet - start checking buildings!</span>
                )}
              </span>
            </div>
            <button
              onClick={() => {
                const current = localStorage.getItem('supertalls-progress');
                const data = current ? JSON.parse(current) : null;
                alert(`localStorage Debug:\n\n` +
                  `✅ Storage working: ${!!current}\n` +
                  `📦 Visited buildings: ${data?.visited?.length || 0}\n` +
                  `📝 Notes: ${Object.keys(data?.notes || {}).length}\n` +
                  `🕐 Last update: ${data?.lastUpdated || 'Never'}\n\n` +
                  `Raw data:\n${current || 'No data'}`);
              }}
              className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
            >
              Debug Storage
            </button>
          </div>
          <div className="text-xs text-gray-500 mt-1 flex items-center gap-4">
            <span>💡 Tip: Export your progress to backup or share with friends!</span>
            <span className="text-green-600">
              {isLoaded ? '🟢 Loaded' : '🟡 Loading...'}
            </span>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="bg-white rounded-lg shadow-lg p-4">
        <div className="flex flex-wrap gap-4 items-center">
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700">Sort by:</label>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as any)}
              className="px-3 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="rank">Rank</option>
              <option value="height">Height</option>
              <option value="visited">Visited Status</option>
            </select>
          </div>
          
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={showCompleted}
              onChange={(e) => setShowCompleted(e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            Show completed
          </label>
        </div>
      </div>

      {/* Buildings List */}
      <div className="space-y-3">
        {filteredBuildings.map((building) => {
          const visited = isVisited(building.rank);
          const visitedData = visitedBuildings.find(v => v.rank === building.rank);
          
          return (
            <div
              key={building.rank}
              className={`bg-white rounded-lg shadow border transition-all duration-200 ${
                visited ? 'bg-green-50 border-green-200' : 'border-gray-200 hover:shadow-md'
              }`}
            >
              <div className="p-4">
                <div className="flex items-start gap-4">
                  {/* Checkbox and Award */}
                  <div className="flex flex-col gap-2">
                    <button
                      onClick={() => toggleVisited(building)}
                      className={`flex-shrink-0 w-6 h-6 rounded border-2 flex items-center justify-center transition-colors ${
                        visited
                          ? 'bg-green-600 border-green-600 text-white'
                          : 'border-gray-300 hover:border-blue-500'
                      }`}
                    >
                      {visited && <Check size={16} />}
                    </button>
                    
                    <button
                      onClick={() => toggleAwarded(building)}
                      className={`flex-shrink-0 w-6 h-6 rounded border-2 flex items-center justify-center transition-colors ${
                        isAwarded(building.rank)
                          ? 'bg-yellow-500 border-yellow-500 text-white'
                          : 'border-gray-300 hover:border-yellow-500'
                      }`}
                      title="The Chris Morgan Architectural Achievement Award"
                    >
                      {isAwarded(building.rank) && <Trophy size={14} />}
                    </button>
                  </div>

                  {/* Building Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h3 className={`font-bold text-lg ${visited ? 'text-green-900' : 'text-gray-900'}`}>
                          #{building.rank} {building.name}
                          {building.status === 'Under construction' && (
                            <span className="ml-2 px-2 py-1 text-xs font-medium bg-orange-100 text-orange-800 rounded-full">
                              🚧 Under Construction
                            </span>
                          )}
                          {isAwarded(building.rank) && (
                            <span className="ml-2 px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-800 rounded-full">
                              🏆 Chris Morgan Architectural Achievement Award
                            </span>
                          )}
                          {visited && <span className="ml-2 text-green-600">✓</span>}
                        </h3>
                        <p className="text-sm text-gray-600 flex items-center gap-1">
                          <MapPin size={14} />
                          {building.address}
                        </p>
                      </div>
                      
                      {building.image_url && (
                        <img
                          src={building.image_url}
                          alt={building.name}
                          className="w-16 h-16 object-cover rounded-md flex-shrink-0"
                          loading="lazy"
                        />
                      )}
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm mb-3">
                      <div>
                        <span className="text-gray-500">Height:</span>
                        <div className="font-medium">{building.height_meters}m ({building.height_feet}ft)</div>
                      </div>
                      <div>
                        <span className="text-gray-500">Floors:</span>
                        <div className="font-medium">{building.floors}</div>
                      </div>
                      <div>
                        <span className="text-gray-500">Status:</span>
                        <div className="font-medium">
                          {building.status === 'Under construction' ? (
                            <div>
                              <span className="px-2 py-1 text-xs font-medium bg-orange-100 text-orange-800 rounded-full">
                                Under Construction
                              </span>
                              {building.expected_completion && (
                                <div className="text-xs text-gray-500 mt-1">
                                  Est. {building.expected_completion}
                                </div>
                              )}
                            </div>
                          ) : (
                            <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">
                              Completed {building.year_completed}
                            </span>
                          )}
                        </div>
                      </div>
                      <div>
                        <span className="text-gray-500">Purpose:</span>
                        <div className="font-medium">{building.purpose}</div>
                      </div>
                    </div>

                    {visited && visitedData && (
                      <div className="text-xs text-green-700 mb-2 flex items-center gap-1">
                        <Clock size={12} />
                        Visited on {new Date(visitedData.visitedAt).toLocaleDateString()}
                      </div>
                    )}

                    {/* Notes */}
                    <div>
                      <textarea
                        value={notes[building.rank] || ''}
                        onChange={(e) => updateNotes(building.rank, e.target.value)}
                        placeholder={visited ? "Add notes about your visit..." : "Notes or observations..."}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded resize-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        rows={2}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {filteredBuildings.length === 0 && !showCompleted && (
        <div className="text-center py-8 text-gray-500">
          <Check className="mx-auto mb-2" size={48} />
          <p>All buildings visited! Toggle "Show completed" to see your progress.</p>
        </div>
      )}
    </div>
  );
}